import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { LuLoaderCircle } from "react-icons/lu";
import { RiAddLargeLine } from "@remixicon/react";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";

interface FileAttachmentHandlerProps {
  isFileUploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  acceptableFileTypes?: string; // e.g. "image/*,.pdf,.doc"
  multiple?: boolean; // Control whether multiple file selection is allowed
  className?: string; // Optional className for the Button
  // Render prop to provide a custom trigger UI instead of the default icon button
  renderTrigger?: (args: {
    onClick: () => void;
    disabled: boolean;
    isFileUploading: boolean;
  }) => React.ReactNode;
}

const FileAttachmentHandler: React.FC<FileAttachmentHandlerProps> = ({
  isFileUploading,
  fileInputRef,
  onFileChange,
  disabled,
  acceptableFileTypes = "image/*", // Default to images if not specified
  multiple = true, // Default to true for backward compatibility
  className = "", // Optional className for the Button
  renderTrigger,
}) => {
  const isDisabled = disabled;
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const handleClick = () => {
    fileInputRef.current?.click();
    setTooltipOpen(false);
  };

  return (
    <div className="flex flex-col items-center">
      <input
        type="file"
        multiple={multiple}
        accept={acceptableFileTypes}
        ref={fileInputRef}
        onChange={onFileChange}
        className="hidden"
      />
      {renderTrigger ? (
        <>
          {renderTrigger({
            onClick: handleClick,
            disabled: !!isDisabled,
            isFileUploading,
          })}
        </>
      ) : (
        <SqTooltipProvider>
          <SqTooltip open={tooltipOpen}>
            <SqTooltipTrigger asChild>
              <Button
                className={`p-0 !w-[14px] !h-[14px] hover:bg-transparent cursor-pointer ${className}`}
                variant="ghost"
                onClick={handleClick}
                disabled={isDisabled}
                onMouseEnter={() => setTooltipOpen(true)}
                onMouseLeave={() => setTooltipOpen(false)}
              >
                {isFileUploading ? (
                  <LuLoaderCircle
                    color="#9AA3BD"
                    className="animate-spin shrink-0"
                    size={16}
                  />
                ) : (
                  <RiAddLargeLine size={14} color="#9AA3BD" className="shrink-0" />
                )}
              </Button>
            </SqTooltipTrigger>

            <SqTooltipContent side="top" align="end">
              Attach
            </SqTooltipContent>
          </SqTooltip>
        </SqTooltipProvider>
      )}
    </div>
  );
};

export default FileAttachmentHandler;
